// ============================================================================
// INVITE TOKEN STORAGE
// Use localStorage key "pending_invite_token" so token survives email confirmation
// redirect. After login/signup + confirm, user is brought back to /invite?token=...
// until they accept, then we clear and route to the correct destination.
// ============================================================================

const INVITE_TOKEN_KEY = "pending_invite_token";

/** True only for an invite that can still be accepted. Dead tokens must not block sign-in. */
export function isLiveInvite(inv: {
  accepted_at?: string | null;
  revoked_at?: string | null;
  expires_at?: string | null;
} | null | undefined): boolean {
  if (!inv) return false;
  if (inv.accepted_at) return false;
  if (inv.revoked_at) return false;
  if (inv.expires_at) {
    const exp = Date.parse(String(inv.expires_at));
    if (Number.isFinite(exp) && exp <= Date.now()) return false;
  }
  return true;
}

/**
 * Store invite token in localStorage (persists across tabs so post-confirm redirect has it).
 * Trims token so URL or copy-paste whitespace doesn't break accept.
 */
export const storeInviteToken = (token: string): void => {
  try {
    localStorage.setItem(INVITE_TOKEN_KEY, (token ?? "").trim());
  } catch (error) {
    console.error("[storeInviteToken] Error storing token:", error);
  }
};

/**
 * Get invite token from localStorage.
 */
export const getInviteToken = (): string | null => {
  try {
    return localStorage.getItem(INVITE_TOKEN_KEY);
  } catch (error) {
    console.error("[getInviteToken] Error reading token:", error);
    return null;
  }
};

/**
 * Clear invite token from localStorage.
 */
export const clearInviteToken = (): void => {
  try {
    localStorage.removeItem(INVITE_TOKEN_KEY);
  } catch (error) {
    console.error("[clearInviteToken] Error clearing token:", error);
  }
};

